import React from 'react';
import { T, sx } from '../../lib/tokens';

/* ---------- shared label style ---------- */

const labelStyle: React.CSSProperties = {
  ...sx.labelLight,
  display: 'block',
  marginBottom: 6,
};

/* ---------- FieldInput ---------- */

interface FieldInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  mono?: boolean;
  highlight?: boolean;
  placeholder?: string;
  disabled?: boolean;
  type?: string;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
}

export const FieldInput: React.FC<FieldInputProps> = ({
  label,
  value,
  onChange,
  mono = false,
  highlight = false,
  placeholder,
  disabled = false,
  type = 'text',
  style,
  inputStyle,
}) => (
  <div style={style}>
    <label style={labelStyle}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '10px 12px',
        fontSize: 14,
        fontFamily: mono ? "'Geist Mono', monospace" : "'Geist', sans-serif",
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        color: T.graphite,
        background: '#FFFFFF',
        border: `1px solid ${highlight ? T.warn : T.paperLine}`,
        borderRadius: 8,
        outline: 'none',
        transition: 'border-color 150ms ease',
        boxSizing: 'border-box',
        opacity: disabled ? 0.5 : 1,
        ...inputStyle,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = highlight ? T.warn : T.sage;
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = highlight ? T.warn : T.paperLine;
      }}
    />
  </div>
);

/* ---------- FieldSelect ---------- */

interface FieldSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  mono?: boolean;
  highlight?: boolean;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  selectStyle?: React.CSSProperties;
}

export const FieldSelect: React.FC<FieldSelectProps> = ({
  label,
  value,
  onChange,
  options,
  mono = false,
  highlight = false,
  disabled = false,
  placeholder,
  style,
  selectStyle,
}) => (
  <div style={style}>
    <label style={labelStyle}>{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '10px 12px',
        fontSize: 14,
        fontFamily: mono ? "'Geist Mono', monospace" : "'Geist', sans-serif",
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        color: T.graphite,
        background: '#FFFFFF',
        border: `1px solid ${highlight ? T.warn : T.paperLine}`,
        borderRadius: 8,
        outline: 'none',
        appearance: 'none',
        WebkitAppearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        paddingRight: 36,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'border-color 150ms ease',
        boxSizing: 'border-box',
        opacity: disabled ? 0.5 : 1,
        ...selectStyle,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = highlight ? T.warn : T.sage;
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = highlight ? T.warn : T.paperLine;
      }}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);
